import { Container, IconButton, Typography } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import type { Accept } from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Button from '@/components/button'
import { useGetCogUrlFromDiagnosis } from '@/hooks/queries/use-get-cog-url-from-diagnosis'
import { useGetDiagnosisById } from '@/hooks/queries/use-get-diagnosis-by-id'
import { useGetLatestDiagnosisReport } from '@/hooks/queries/use-get-latest-diagnosis-report'
import { useGetPerimeterData } from '@/hooks/queries/use-get-perimeter-data'
import { CustomDropzone } from '@/pages/demands/steps/select-files/components/custom-dropzone'
import { shpFilesToZip } from '@/utils/file-conversions'

import { LinearProgressWithLabel } from '../../components/linear-progress-with-label'
import { Map } from '../../components/map'
import { ReportIssues } from '../../components/report-issues'
import { useDemandAnalysisStore } from '../../contexts/demand-analysis-store-context'
import { useDemandAnalysisRoute } from '../../hooks/use-demand-analysis-route'
import {
  SelectFilesSchema,
  useSelectFilesForm,
} from './hooks/use-select-files-form'
import { useStyles } from './styles'

interface InputProps {
  name: 'perimeter' | 'mosaic'
  inputName: 'mosaic.input' | 'perimeter.input'
  title: string
  accept: Accept
  label?: string
  modificationFlag: boolean
  maxFiles?: number
  errors: (string | undefined)[]
  onClear: () => void
}

export function SelectFiles() {
  const classes = useStyles()
  const navigate = useNavigate()

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useSelectFilesForm()

  const [
    mosaicModificationFlag,
    mosaicInput,
    perimeterModificationFlag,
    perimeterResults,
  ] = watch([
    'mosaic.modification_flag',
    'mosaic.input',
    'perimeter.modification_flag',
    'perimeter.results',
  ])

  const { treatment, culture, diagnosisId, makeUrl } = useDemandAnalysisRoute()

  const { data: diagnosis } = useGetDiagnosisById(
    treatment!,
    culture!,
    diagnosisId!,
  )
  const { data: cogUrl } = useGetCogUrlFromDiagnosis(culture!, diagnosisId!)
  const { data: perimeterData } = useGetPerimeterData(diagnosisId!)
  const { data: report } = useGetLatestDiagnosisReport(
    treatment!,
    culture!,
    diagnosisId!,
  )

  const { setPerimeter, setMosaic } = useDemandAnalysisStore((state) => ({
    setPerimeter: state.setPerimeter,
    setMosaic: state.setMosaic,
  }))

  const mosaicFromInput = mosaicInput?.[0]
  const perimeter = perimeterModificationFlag
    ? perimeterResults?.geojson
    : perimeterData

  async function onSubmit(data: SelectFilesSchema) {
    if (!data.mosaic?.modification_flag && !data.perimeter?.modification_flag) {
      return toast.error(
        'Nenhum arquivo foi modificado. Por favor, faça uma alteração para continuar.',
      )
    }

    try {
      if (data.perimeter?.modification_flag) {
        const zip = await shpFilesToZip(data.perimeter.input, {
          type: 'blob',
          filename: data.perimeter.results.filename,
        })

        setPerimeter({
          zip,
          shapefile: data.perimeter.input,
          results: {
            ...data.perimeter.results,
            centroid: data.perimeter.results.lat_long,
          },
        })
      }

      if (data.mosaic?.modification_flag) {
        setMosaic({
          input: data.mosaic.input[0],
          results: data.mosaic.results,
        })
      }

      navigate(makeUrl('resume'))
    } catch (error: any) {
      toast.error(
        `Erro ao salvar os arquivos. [${
          error?.message || 'Erro desconhecido'
        }]`,
      )
    }
  }

  function renderInput(props: InputProps) {
    return props.modificationFlag ? (
      <CustomDropzone
        key={props.name}
        className={classes.dropzone}
        control={control}
        name={props.inputName}
        label={props.title}
        accept={props.accept}
        errors={props.errors}
        maxFiles={props.maxFiles}
      />
    ) : (
      <LinearProgressWithLabel
        key={props.name}
        title={props.title}
        label={props.label}
        progressValue={100}
        onClearFiles={props.onClear}
      />
    )
  }

  return (
    <Container
      className={classes.container}
      component='form'
      onSubmit={handleSubmit(onSubmit, console.error)}
    >
      <header className={classes.header}>
        <IconButton
          size='small'
          onClick={() => navigate(-1)}
          color='secondary'
          aria-label='Voltar'
        >
          <ArrowBack />
        </IconButton>

        <div>
          <Typography variant='h2'>Editar arquivos</Typography>
          <Typography variant='h5'>
            Analise os pontos de atenção do diagnóstico
          </Typography>
        </div>
      </header>

      <div className={classes.content}>
        <div className={classes.resume}>
          <Typography component={'span'}>Arquivos</Typography>
          <div>
            <ReportIssues report={report} />
            {!perimeterData || !(mosaicFromInput || cogUrl) ? (
              <Skeleton
                role='progressbar'
                width={'100%'}
                height={'100%'}
                variant='rect'
                style={{ borderRadius: 4, minHeight: 0 }}
              />
            ) : (
              <div className={classes.files}>
                {renderInput({
                  name: 'mosaic',
                  inputName: 'mosaic.input',
                  title: 'Ortomosaico - .tif',
                  accept: { 'image/tiff': ['.tif'] } as Accept,
                  label: diagnosis?.imageName,
                  modificationFlag: Boolean(mosaicModificationFlag),
                  maxFiles: 1,
                  errors: [errors?.mosaic?.results?.message],
                  onClear: () => {
                    setValue('mosaic.modification_flag', true)
                  },
                })}

                {renderInput({
                  name: 'perimeter',
                  inputName: 'perimeter.input',
                  title: 'Perímetro - .shp + .shx + .dbf + .prj + .cpg ou .kml',
                  label: ['shp', 'shx', 'dbf', 'prj', 'cpg']
                    .map((ext) => `${perimeterData?.fileName}.${ext}`)
                    .join(', '),
                  accept: {
                    'application/octet-stream': [
                      '.shp',
                      '.shx',
                      '.dbf',
                      '.prj',
                      '.cpg',
                    ],
                    'application/vnd.google-earth.kml+xml': ['.kml'],
                  } as Accept,
                  modificationFlag: Boolean(perimeterModificationFlag),
                  errors: [errors.perimeter?.results?.message],
                  onClear: () => {
                    setValue('perimeter.modification_flag', true)
                  },
                })}
              </div>
            )}
          </div>
        </div>
        <div className={classes.map}>
          <Typography component={'span'}>Área cadastrada</Typography>

          <div>
            {!perimeterData || !(mosaicFromInput || cogUrl) ? (
              <Skeleton
                role='progressbar'
                width={'100%'}
                height={'100%'}
                variant='rect'
                style={{ borderRadius: 4 }}
              />
            ) : (
              <Map
                perimeter={perimeter}
                thumbnail={mosaicFromInput}
                cogUrl={cogUrl}
              />
            )}
          </div>
        </div>
      </div>

      <footer className={classes.footer}>
        <Button outlined onClick={() => navigate(-1)}>
          Voltar
        </Button>
        <Button type='submit'>Avançar</Button>
      </footer>
    </Container>
  )
}
